import CompletedPathways from '@assets/icons/learning-gamification/dashboard-completed-patways.svg';
import Points from '@assets/icons/learning-gamification/dashboard-points.svg';
import Ranking from '@assets/icons/learning-gamification/dashboard-ranking.svg';
import InfoIcon from '@assets/icons/tooltip-icon.svg';
import { BaseSpin, BaseTooltip } from '@components';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { TooltipContent } from '@routes/___shared___/styled.tsx';
import { Title } from '@routes/sidebar/learning/ui/dashboard-tab/___shared___/styled.tsx';
import {
    GamificationSectionWrapper,
    MainGamificationSectionWrapper,
} from '@routes/sidebar/learning/ui/dashboard-tab/gamification-section/styled.ts';
import type { Props } from '@routes/sidebar/learning/ui/dashboard-tab/gamification-section-item';
import { GamificationSectionItem } from '@routes/sidebar/learning/ui/dashboard-tab/gamification-section-item';
import { translateItem } from '@utils/translations.ts';

import type { CHANGE_TYPE } from '../___shared___/utils.ts';
import { STATISTICS_CHANGE_TYPE } from '../___shared___/utils.ts';

export const SHARED_GAMIFICATION_SECTION_LEADERBOARD = msg`Leaderboard`;

interface GamificationChange {
    changeText: string;
    changeType: CHANGE_TYPE;
}

export const GamificationSection = () => {
    const statisticsSummary = useAppSelector((state) => state.statistics.statisticsSummary);

    const { t } = useLingui();
    const TOOLTIP_TITLE = t`Shows your training progress, points, and ranking among others with the same position in your company. Includes Mandatory, Recommended, and Library training.`;

    const getGamificationChange = (oldValue: number, currentValue: number): GamificationChange => {
        let changeText = t`no change`;
        let changeType: CHANGE_TYPE = STATISTICS_CHANGE_TYPE.NEUTRAL;
        const valueChange = currentValue - oldValue;
        if (valueChange !== 0) {
            //if different than 0 its negative or positive change
            const sign = valueChange < 0 ? '-' : '+';
            changeType = valueChange < 0 ? STATISTICS_CHANGE_TYPE.DECREASE : STATISTICS_CHANGE_TYPE.INCREASE;
            changeText = t`${sign}${Math.abs(valueChange)} this month`;
        }
        return {
            changeText,
            changeType,
        };
    };

    let pathwayChange = getGamificationChange(0, 0);
    let rankingChange = getGamificationChange(0, 0);
    let pointsChange = getGamificationChange(0, 0);

    let rankingTextValue = '';
    if (statisticsSummary) {
        pathwayChange = getGamificationChange(
            statisticsSummary.totalCompletedLastMonth,
            statisticsSummary.totalCompleted,
        );
        rankingChange = getGamificationChange(
            statisticsSummary.rankInfo.userPositionLastMonth,
            statisticsSummary.rankInfo.userPosition,
        );
        pointsChange = getGamificationChange(
            statisticsSummary.rankInfo.scoreLastMonth,
            statisticsSummary.rankInfo.score,
        );
        rankingTextValue = `${statisticsSummary.rankInfo.userPosition}/${statisticsSummary.rankInfo.totalPlayers}`;
    }

    const gamificationSection: Props[] = [
        {
            id: 1,
            title: t`Completed pathways`,
            value: (statisticsSummary?.totalCompleted ?? 0).toString(),
            changeText: pathwayChange.changeText,
            changeType: pathwayChange.changeType,
            icon: <CompletedPathways />,
        },
        {
            id: 2,
            title: t`Ranking`,
            value: rankingTextValue,
            changeText: rankingChange.changeText,
            changeType: rankingChange.changeType,
            icon: <Ranking />,
        },
        {
            id: 3,
            title: t`BlueByte points`,
            value: `${statisticsSummary?.rankInfo.score}`,
            changeText: pointsChange.changeText,
            changeType: pointsChange.changeType,
            icon: <Points />,
        },
    ];
    if (!statisticsSummary) {
        return <BaseSpin size="medium" />;
    }
    return (
        <MainGamificationSectionWrapper>
            <Title>
                {translateItem(SHARED_GAMIFICATION_SECTION_LEADERBOARD)}
                <BaseTooltip
                    title={<TooltipContent>{TOOLTIP_TITLE}</TooltipContent>}
                    placement="right"
                    arrow={false}
                    icon={<InfoIcon />}
                />
            </Title>
            <GamificationSectionWrapper>
                {gamificationSection.map((item) => (
                    <GamificationSectionItem key={item.id} {...item} />
                ))}
            </GamificationSectionWrapper>
        </MainGamificationSectionWrapper>
    );
};
